/**
 * PATH: packages/email-hub/src/feature/components/LiveSearchResults.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-LIVESEARCHRESULTS-001
 * PURPOSE: G2 ? render the live Outlook (Graph $search) fanout below
 *          the local result list: CTA when offered, then
 *          loading/results/empty/error states.
 * WHY: Sparse local recall usually means older mail that never synced.
 *      Rows with a local match (email_id) open in the normal preview;
 *      unsynced rows are informational-only in v1 (a "not synced"
 *      badge ? clicking does nothing until a fetch-on-demand lands).
 * RELATED:
 *  - hooks/useLiveSearch.ts ? state machine + gating (unit-tested).
 *  - components/SearchSyncBanner.tsx ? sibling banner with the same CTA.
 */

import { CloudDownload, Loader2, Paperclip } from 'lucide-react'
import type {
  LiveSearchResponse,
  LiveSearchResultRow,
  LiveSearchStatus,
} from '../hooks/useLiveSearch'

interface LiveSearchResultsProps {
  offered: boolean
  status: LiveSearchStatus
  data: LiveSearchResponse | null
  error: string | null
  onTrigger: () => void
  /** Open a locally-synced email in the normal preview pane. */
  onOpenLocal: (emailId: string) => void
}

function formatReceived(iso: string | null): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

function ResultRow({
  row,
  onOpenLocal,
}: {
  row: LiveSearchResultRow
  onOpenLocal: (emailId: string) => void
}) {
  const openable = row.synced && !!row.email_id
  return (
    <button
      type="button"
      disabled={!openable}
      onClick={() => {
        if (openable && row.email_id) onOpenLocal(row.email_id)
      }}
      title={
        openable
          ? 'Open email'
          : 'Not synced yet ? this email exists in Outlook but has not been imported'
      }
      data-testid="live-search-result-row"
      className={
        'w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs rounded-md border border-transparent ' +
        (openable
          ? 'hover:bg-accent cursor-pointer'
          : 'opacity-70 cursor-default')
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium truncate">
            {row.subject || '(no subject)'}
          </span>
          {row.has_attachments && (
            <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
          )}
        </div>
        <div className="text-[10px] text-muted-foreground truncate">
          {row.from_name || row.from_address}
          {row.received_at ? ` ? ${formatReceived(row.received_at)}` : ''}
        </div>
      </div>
      <span
        data-testid="live-search-sync-badge"
        className={
          'shrink-0 px-1.5 py-0.5 rounded text-[9px] font-medium ' +
          (openable
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300')
        }
      >
        {openable ? 'synced' : 'not synced'}
      </span>
    </button>
  )
}

export function LiveSearchResults({
  offered,
  status,
  data,
  error,
  onTrigger,
  onOpenLocal,
}: LiveSearchResultsProps) {
  if (!offered && status === 'idle') return null

  if (status === 'idle') {
    return (
      <div className="mt-2 px-1" data-testid="live-search-cta">
        <button
          type="button"
          onClick={onTrigger}
          className="flex items-center gap-1.5 px-2 py-1 text-xs rounded-md border border-border text-muted-foreground hover:bg-accent"
        >
          <CloudDownload className="h-3.5 w-3.5" aria-hidden />
          Search Outlook for older mail
        </button>
      </div>
    )
  }

  if (status === 'loading') {
    return (
      <div
        className="flex items-center gap-2 mt-2 px-2 py-1.5 text-xs text-muted-foreground"
        data-testid="live-search-loading"
        role="status"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Searching Outlook?
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div
        className="flex items-center gap-2 mt-2 px-2 py-1.5 text-xs rounded-md border border-red-300/60 bg-red-50 text-red-700 dark:border-red-700/50 dark:bg-red-900/20 dark:text-red-300"
        data-testid="live-search-error"
        role="alert"
      >
        <span className="flex-1">Outlook search failed{error ? `: ${error}` : ''}.</span>
        <button type="button" onClick={onTrigger} className="underline hover:no-underline">
          Retry
        </button>
      </div>
    )
  }

  const rows = data?.results ?? []
  const skipped = data?.accounts_skipped ?? []
  return (
    <div className="mt-2" data-testid="live-search-results">
      <div className="px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        From Outlook (not yet synced)
      </div>
      {rows.length === 0 ? (
        <div className="px-2 py-1.5 text-xs text-muted-foreground" data-testid="live-search-empty">
          No older mail found in Outlook for this search.
        </div>
      ) : (
        <div className="mt-1 space-y-0.5">
          {rows.map((row) => (
            <ResultRow key={row.graph_id} row={row} onOpenLocal={onOpenLocal} />
          ))}
        </div>
      )}
      {skipped.length > 0 && (
        <div
          className="px-2 py-1 text-[10px] text-amber-700 dark:text-amber-300"
          data-testid="live-search-skipped"
        >
          {skipped.length === 1
            ? `${skipped[0].account_email} could not be searched (reconnect may be needed).`
            : `${skipped.length} mailboxes could not be searched (reconnect may be needed).`}
        </div>
      )}
    </div>
  )
}
